import { useCallback } from 'react';
import classNames from 'classnames';
import { usePostHog } from 'posthog-js/react';

import { isRewardDeal } from '../../utils/rewards';

import {
  captureAttributesToProperties,
  getActionType,
  getEventName,
  getTrackerId,
  POSTHOG_CAPTURE_ATTRIBUTES,
  POSTHOG_PROPERTIES,
  TRACKER_IDS,
} from '../../config/tracker';
import { REWARD_AMOUNT_TYPES } from '../../config/rewards';
import IconCircled, { ICON_CIRCLE_SIZE } from '../IconCircled';
import MembershipSVG from '../SvgComponents/Membership_svg';
import TCCoinSvg from '../SvgComponents/TCCoin_svg';

import './RewardsV2Card.scss';
import { formatReadableNumber } from '../../utils/numbers';

const {
  rewards: {
    list: {
      rewardItem: trackerRewardItem,
    },
  },
} = TRACKER_IDS;

const RewardsV2Card = ({
  index,
  rewardItem,
  onClick,
  trackerProps = {},
  backgroundColor,
  activeSubscription,
  cashbackMultiplierValue,
}) => {
  const posthog = usePostHog();

  const {
    description,
    allowTcCredits,
    amountType,
    rewardAmount,
    type,
    store: {
      logoUrl,
      imgUrl,
      tcCreditAmount = '0',
      tcCreditAmountType,
      cardBgColor,
    } = {},
  } = rewardItem;

  const amountText = REWARD_AMOUNT_TYPES[amountType]?.getText({ rewardAmount, type }) ?? '';
  const isDeal = isRewardDeal(rewardItem);
  const appliesMultiplier = activeSubscription && Number(cashbackMultiplierValue) > 1;

  const tcAmount = Number(tcCreditAmount) || 0;
  const hasTcAmount = tcAmount > 0;

  const getTcDisplayText = () => formatReadableNumber(tcAmount);

  const getTcCreditText = () => {
    if (!hasTcAmount) return 'Earn TC';
    if (tcCreditAmountType === 'PERCENTAGE') return `Up to ${formatReadableNumber(tcAmount * 100)}%`;
    if (tcCreditAmountType === 'VALUE') return `Up to $${getTcDisplayText()}`;
    return 'Earn TC';
  };

  const shouldShowTcCredits = Boolean(allowTcCredits) || isDeal;

  const handleOnClick = useCallback(() => {
    posthog.capture(
      getEventName(trackerRewardItem),
      {
        [POSTHOG_PROPERTIES.TRACKER_ID]: getTrackerId(trackerRewardItem),
        [POSTHOG_PROPERTIES.ACTION_ID]: getActionType(trackerRewardItem),
        [POSTHOG_PROPERTIES.REWARD_ID]: rewardItem.uid,
        [POSTHOG_PROPERTIES.REWARD_NAME]: rewardItem.name,
        [POSTHOG_PROPERTIES.REWARD_IS_DEAL]: isDeal,
        ...(index != null && !Number.isNaN(Number(index)) && {
          [POSTHOG_PROPERTIES.LIST_POSITION]: Number(index) + 1,
        }),
        ...captureAttributesToProperties(trackerProps),
      },
    );

    onClick?.(rewardItem, false, index);
  }, [rewardItem.uid, onClick, isDeal, index, trackerProps]);

  return (
    <button
      className={classNames('RewardsV2Card', { activeSubscription })}
      style={backgroundColor ? { backgroundColor } : undefined}
      onClick={handleOnClick}
      type="button"
      aria-label="reward card"
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          handleOnClick();
        }
      }}
      {...{
        [POSTHOG_CAPTURE_ATTRIBUTES.TRACKER_ID]: getTrackerId(trackerRewardItem),
        [POSTHOG_CAPTURE_ATTRIBUTES.ACTION_ID]: getActionType(trackerRewardItem),
        [POSTHOG_CAPTURE_ATTRIBUTES.REWARD_ID]: rewardItem.uid,
        [POSTHOG_CAPTURE_ATTRIBUTES.REWARD_NAME]: rewardItem.name,
        [POSTHOG_CAPTURE_ATTRIBUTES.REWARD_IS_DEAL]: isDeal,
        ...(!Number.isNaN(index) && {
          [POSTHOG_CAPTURE_ATTRIBUTES.LIST_POSITION]: Number(index) + 1,
        }),
        ...trackerProps,
      }}
    >
      <div
        className="RewardsV2Card__top"
        alt="reward image"
        style={{
          backgroundColor: cardBgColor,
          backgroundImage: `url(${imgUrl})`,
        }}
      />
      <div className="RewardsV2Card__center">
        <IconCircled
          size={ICON_CIRCLE_SIZE.MEDIUM_LARGE}
          icon={(
            <img
              src={logoUrl}
              alt="Sweepstakes"
            />
          )}
          backgroundColor={cardBgColor}
          borderColor="var(--ui-gray-light)"
        />
      </div>
      <div className="RewardsV2Card__bottom">
        <p className="RewardsV2Card__bottom-type">{amountText}</p>
        <p className="RewardsV2Card__bottom-description">{description}</p>
        {shouldShowTcCredits && (
          <div className="RewardsV2Card__bottom-tc-credits">
            <p className={appliesMultiplier ? 'RewardsV2Card__bottom-multiplier' : 'RewardsV2Card__bottom-tc-credits-value'}>
              <span className="RewardsV2Card__bottom-tc-credits-number">
                <TCCoinSvg width={10} height={10} />
                {getTcCreditText()}
              </span>
            </p>
            {appliesMultiplier && hasTcAmount && (
              <p className="RewardsV2Card__bottom-multiplier">
                <MembershipSVG width={10} height={10} color="var(--membership-ice-pink)" />
                {` Unlimited Earns ${cashbackMultiplierValue}X`}
              </p>
            )}
          </div>
        )}
      </div>
    </button>
  );
};

export default RewardsV2Card;
